import { useGetCountryName } from 'hooks/ppoints/useGetCountryName';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const MARKET_CREATED_POLL_INTERVAL_MS = 2000;
const MARKET_CREATED_MAX_POLLS = 5;

/**
 * Countries with no Shopify market yet, styled like `ShippingZoneRateStep`'s `NO_ZONE_WARNING`
 * banner (warning banner, body copy, a list of the affected countries) instead of the settings
 * page's plain `PickupPointSettingsWithoutMarket` section — same "add a market" action underneath,
 * since (unlike a shipping zone) a market isn't created per country.
 *
 * A market created through the intent isn't necessarily visible to the markets query straight away,
 * so once the intent reports success `onMarketCreated` is polled every 2 seconds until the new market
 * shows up — i.e. the country list changes, or every country has a market and this banner unmounts —
 * for at most 5 calls.
 */
const MarketRateWithoutMarketBanner: React.FC<MarketRateWithoutMarketBannerProps> = ({
    countryCodes,
    onMarketCreated,
}) => {
    const { t } = useTranslation();
    const { getTranslatedCountryName } = useGetCountryName();
    const [isCreating, setIsCreating] = useState(false);
    const [marketCreated, setMarketCreated] = useState(false);

    // Kept in a ref so a parent re-render handing over a new callback doesn't restart the interval.
    const onMarketCreatedRef = useRef(onMarketCreated);
    onMarketCreatedRef.current = onMarketCreated;

    const countriesSignature = countryCodes.join(',');

    // The country list changing means the polled refetch picked up the new market — stop polling.
    useEffect(() => {
        setMarketCreated(false);
    }, [countriesSignature]);

    // Gives up after `MARKET_CREATED_MAX_POLLS` calls (the immediate one included), so a market that
    // never shows up here — e.g. one created for a country not in this list — doesn't poll forever.
    useEffect(() => {
        if (!marketCreated) return;

        let polls = 1;
        onMarketCreatedRef.current?.();

        const interval = setInterval(() => {
            onMarketCreatedRef.current?.();
            polls++;

            if (polls >= MARKET_CREATED_MAX_POLLS) {
                clearInterval(interval);
                setMarketCreated(false);
            }
        }, MARKET_CREATED_POLL_INTERVAL_MS);

        return () => clearInterval(interval);
    }, [marketCreated]);

    const openCreateMarket = async () => {
        setIsCreating(true);

        try {
            const activity = await shopify.intents.invoke?.('create:shopify/Market');
            const response = await activity?.complete;

            if (response?.code === 'ok') setMarketCreated(true);
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <div className="pt-2">
            <s-banner tone="warning" heading={t('WIZARD.SHIPPING_ZONE_RATE_STEP.NO_ZONE_WARNING.HEADING')}>
                <s-text>{t('SHIPPING_PROVIDERS.PICKUP_POINT_SETTINGS.WITHOUT_MARKET.DESCRIPTION')}</s-text>
                <div className="grid gap-2 pt-2">
                    {countryCodes.map((countryCode) => (
                        <s-text key={countryCode}>{getTranslatedCountryName(countryCode)}</s-text>
                    ))}
                </div>
                <div className="flex flex-row pt-2">
                    <s-button icon="plus" loading={isCreating || marketCreated || undefined} onClick={openCreateMarket}>
                        {t('SHIPPING_PROVIDERS.PICKUP_POINT_SETTINGS.WITHOUT_MARKET.ADD_MARKET')}
                    </s-button>
                </div>
            </s-banner>
        </div>
    );
};

interface MarketRateWithoutMarketBannerProps {
    countryCodes: string[];
    onMarketCreated?: () => void;
}

export default MarketRateWithoutMarketBanner;
